<template>
  <div class="flex flex-col text-blue-800">
    <div v-if="!isLoading && getOrder" class="grid grid-cols-12">
      <div class="flex flex-col px-6 col-span-7">
        <div class="my-4 px-4 bg-gray-200">
          <production-order-detail
            v-if="getOrder"
            :order="getOrder"
            :is-rtw-tailoring="isRtwTailoring"
          />
        </div>

        <div v-if="getOrder.match.status === 'ERROR'">
          {{ $t("production_order_detail.errors.incomplete_match_data") }}
        </div>
        <div v-else class="flex flex-col space-y-2 items-center" id="datatable">
          <production-order-body-table
            :adjustments="adjustments"
            :adjustments-loading="adjustmentsLoading"
            :adjustments-error="adjustmentsError"
          />
          <production-order-garment-table
            :adjustments="adjustments"
            :fit-adjustments="fitAdjustments"
            :adjustments-loading="adjustmentsLoading"
            :adjustments-error="adjustmentsError"
            :is-rtw-tailoring="isRtwTailoring"
            :can-edit="true"
          />
        </div>
        <div class="col-span-4 bg-gray-200 mt-4 p-4">
          <div class="flex flex-row justify-center">
            <button
              class="button py-2 px-8 w-40 bg-red-700 text-white mx-2"
              @click="showReturnDialog = true"
            >
              {{ $t("production_order_detail.buttons.return") }}
            </button>
            <button
              class="button py-2 px-8 w-40 bg-yellow-700 text-white mx-2"
              @click="showCommentModal = true"
            >
              {{ $t("production_order_detail.buttons.comments") }}
            </button>
            <button
              v-if="getOrder.match.status !== 'ERROR'"
              v-bind:class="[
                'button py-2 px-8 w-40 text-white mx-2',
                canSaveOrFinishOrder() ? 'bg-blue-800' : 'bg-gray-400'
              ]"
              class=""
              @click="showSaveDialog = true"
              :disabled="!canSaveOrFinishOrder()"
            >
              {{ $t("production_order_detail.buttons.save") }}
            </button>
            <button
              v-if="getOrder.match.status !== 'ERROR'"
              class="button py-2 px-8 bg-yellow-700 text-white mx-2"
              @click="showExportDialog = true"
            >
              {{ $t("production_order_detail.buttons.export") }}
            </button>
            <button
              v-if="getOrder.match.status !== 'ERROR'"
              v-bind:class="[
                'button py-2 px-8 w-40 text-white mx-2',
                canSaveOrFinishOrder() ? 'bg-blue-800' : 'bg-gray-400'
              ]"
              @click="showFinishDialog = true"
              :disabled="!canSaveOrFinishOrder()"
            >
              {{ $t("production_order_detail.buttons.finish") }}
            </button>
          </div>
        </div>
      </div>
      <div class="flex w-full col-span-5 bg-gray-200">
        <div class="grid grid-cols-6 p-6 w-full" id="images">
          <div class="flex flex-col col-span-3 justify-center items-center">
            <p class="font-bold text-2xl pb-2">
              {{ $t("production_order_detail.back_view") }}
            </p>
            <bodyprint-view
              :image-source="this.getImageBack"
              :is-active="true"
              side="back"
              :flipped="false"
              :picture-height="667"
              :picture-width="500"
              :back-guide="getCropBack"
              :side-guide="getCropSide"
              :show-active-line="false"
            />
          </div>
          <div class="flex flex-col col-span-3 justify-center items-center">
            <p class="font-bold text-2xl pb-2">
              {{ $t("production_order_detail.side_view") }}
            </p>
            <bodyprint-view
              :image-source="this.getImageSide"
              :is-active="true"
              side="side"
              :flipped="false"
              :picture-height="667"
              :picture-width="500"
              :back-guide="getCropBack"
              :side-guide="getCropSide"
              :show-active-line="false"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="flex flex-col mx-auto p-4 w-full h-full items-center text-blue-800 text-2xl my-12"
      style="text-align: center"
    >
      {{ $t("production_order_detail.loading") }}
    </div>
    <Dialog
      v-if="showReturnDialog"
      @confirmed="returnFromOrder"
      @close="showReturnDialog = false"
      color="red"
      :title="$t('production_order_detail.dialogs.return.title').toString()"
      :message="$t('production_order_detail.dialogs.return.message').toString()"
    />
    <Dialog
      v-if="showSaveDialog"
      @confirmed="saveOrder"
      @close="showSaveDialog = false"
      :title="$t('production_order_detail.dialogs.save.title').toString()"
      :message="$t('production_order_detail.dialogs.save.message').toString()"
    />
    <ProductionOrderExportSelectionDialog
      v-if="showExportDialog"
      @confirmed="handleExport"
      @close="showExportDialog = false"
    />
    <Dialog
      v-if="showFinishDialog"
      @confirmed="finishOrder"
      @close="showFinishDialog = false"
      :title="$t('production_order_detail.dialogs.finish.title').toString()"
      :message="$t('production_order_detail.dialogs.finish.message').toString()"
    />
    <CommentModal
      v-if="getOrder"
      :comments="getOrder.comments"
      :showModal="showCommentModal"
      :onClose="() => (showCommentModal = false)"
      :onSubmit="
        text => {
          addComment(text);
        }
      "
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { ROUTE_PRODUCTION } from "@/router";
import BodyprintView from "@/components/bodyprint/bodyprint-view.vue";
import Dialog from "@/components/dialog.vue";
import ProductionOrderGarmentTable from "@/components/production-order/production-order-garment-table.vue";
import ProductionOrderDetail from "@/components/production-order/production-order-detail.vue";
import ProductionOrderBodyTable from "@/components/production-order/production-order-body-table.vue";
import CommentModal from "@/components/modals/comment-modal.vue";
import ProductionOrderExportSelectionDialog from "@/components/production-order/export/production-order-export-selection-dialog.vue";
import { RetailerOrderStatus } from "@/enums";
import {
  exportProductionOrderToPdf,
  exportProductionOrderToCsv,
  calculateOrderAdjustments
} from "@/utils/production-order";

export default {
  name: "ProductionOrderEditView",
  components: {
    ProductionOrderExportSelectionDialog,
    CommentModal,
    ProductionOrderBodyTable,
    ProductionOrderGarmentTable,
    Dialog,
    BodyprintView,
    ProductionOrderDetail
  },
  created() {
    this.getViewData();

    window.addEventListener("resize", this.setImageWindowHeight);
    window.addEventListener("keydown", this.handleKeydown, null);
  },
  beforeDestroy() {
    window.removeEventListener("resize", () => {});
    window.removeEventListener("keydown", this.handleKeydown);
  },
  computed: {
    ...mapGetters({
      getAuthLoggedIn: "auth/getAuthLoggedIn",
      getAuth: "auth/getAuth",
      getSidebarOpen: "layout/getSidebarOpen",
      getOrder: "retailerOrders/getOrder",
      getImageSide: "retailerOrders/getImageSide",
      getImageBack: "retailerOrders/getImageBack",
      getPictureHeight: "retailerOrders/getPictureHeight",
      getPictureWidth: "retailerOrders/getPictureWidth",
      getCropBack: "retailerOrders/getCropBack",
      getCropSide: "retailerOrders/getCropSide",
      getClothingSizeItemDefaults: "configuration/getClothingSizeItemDefaults"
    }),
    isRtwTailoring() {
      if (!this.getOrder) return false;
      return this.getOrder.tailoring === "RtW";
    }
  },
  data() {
    return {
      showSaveDialog: false,
      showReturnDialog: false,
      showExportDialog: false,
      showFinishDialog: false,
      isLoading: true,
      adjustmentsError: "",
      adjustmentsLoading: true,
      adjustments: [],
      fitAdjustments: [],
      showCommentModal: false,
      previousOrderStatus: null
    };
  },
  methods: {
    ...mapMutations({
      setWindowHeight: "retailerOrders/setWindowHeight",
      setCrop: "retailerOrders/setCrop"
    }),
    ...mapActions(["auth/authCheck", "layout/toggleSidebar"]),
    canSaveOrFinishOrder() {
      return (
        this.getOrder.status === RetailerOrderStatus.NEW ||
        this.getOrder.status === RetailerOrderStatus.IN_REVIEW
      );
    },
    getViewData() {
      const orderId = this.$route.params.order_id;
      const storageKey = `order_previous_status_${orderId}`;

      this.$store
        .dispatch("retailerOrders/requestOrderToView", orderId)
        .then(() => {
          var order = this.$store.getters["retailerOrders/getOrder"];
          this.$store
            .dispatch("retailerOrders/getBlurredImages", order.id)
            .then(() => {
              const savedStatus = localStorage.getItem(storageKey);
              this.previousOrderStatus = savedStatus || order.status;

              if (
                order.status === RetailerOrderStatus.NEW ||
                order.status === RetailerOrderStatus.IN_PROGRESS
              ) {
                if (!savedStatus) {
                  localStorage.setItem(storageKey, order.status);
                }
                // Update the status to IN_REVIEW
                this.$store
                  .dispatch("retailerOrders/updateOrderStatus", {
                    id: order.id,
                    status: RetailerOrderStatus.IN_REVIEW
                  })
                  .then(() => {
                    this.$store
                      .dispatch("retailerOrders/prepareOrderView")
                      .then(() => {
                        this.prepareView();
                      });
                  });
              } else {
                this.$store
                  .dispatch("retailerOrders/prepareOrderView")
                  .then(() => {
                    this.prepareView();
                  });
              }
            })
            .catch(() => {
              this.$router.replace({ name: ROUTE_PRODUCTION });
            });
        });
    },
    async prepareView() {
      if (!this.getOrder) return;

      try {
        await this.$store.dispatch(
          "configuration/getClothingSizeItemsDefaults"
        );

        this.isLoading = false;
        this.setImageWindowHeight();

        const result = await calculateOrderAdjustments(
          this.getOrder,
          this.getClothingSizeItemDefaults,
          this.$t.bind(this)
        );

        this.adjustments = result.adjustments;
        this.fitAdjustments = result.fitAdjustments;
        this.adjustmentsError = this.$t(result.error);
        this.adjustmentsLoading = false;
      } catch (error) {
        this.adjustmentsError = this.$t(
          "production_order_detail.errors.incomplete_fit_specification_data"
        );
        this.adjustmentsLoading = false;
      }
    },
    setImageWindowHeight() {
      let payload = {
        height: window.innerHeight,
        width: window.innerWidth
      };
      this.setWindowHeight(payload);
      this.setProjectionCrop(
        this.getOrder.queue_item.bodyprint.guides[0].crop,
        "back"
      );
      this.setProjectionCrop(
        this.getOrder.queue_item.bodyprint.guides[1].crop,
        "left"
      );
    },
    setProjectionCrop(crop, projection) {
      let updatedCrop = {
        x: crop.x,
        x2: crop.x + crop.width,
        y: crop.y,
        y2: crop.y + crop.height,
        width: crop.width,
        height: crop.height
      };
      this.setCrop([updatedCrop, projection]);
    },
    toListScreen() {
      localStorage.removeItem(
        `order_previous_status_${this.$route.params.order_id}`
      );
      this.$router.replace({
        name: ROUTE_PRODUCTION
      });
      this.$store.dispatch("retailerOrders/resetOrder");
      this.$store.dispatch("layout/toggleSidebar");
    },
    addComment(text) {
      if (text === "") return;
      this.$store
        .dispatch("retailerOrders/addComment", {
          id: this.getOrder.id,
          text: text
        })
        .then(() => {
          this.getViewData();
        });
    },
    returnFromOrder() {
      const doReturn = () => {
        this.showReturnDialog = false;
        this.toListScreen();
      };

      if (
        this.getOrder.status === RetailerOrderStatus.IN_PRODUCTION ||
        this.getOrder.status === RetailerOrderStatus.SENT_TO_CUSTOMER
      ) {
        doReturn();
      } else {
        if (this.previousOrderStatus === RetailerOrderStatus.NEW) {
          this.$store
            .dispatch("retailerOrders/updateOrderStatus", {
              id: this.getOrder.id,
              status: RetailerOrderStatus.NEW
            })
            .then(() => {
              doReturn();
            });
        } else if (
          this.previousOrderStatus === RetailerOrderStatus.IN_PROGRESS ||
          this.previousOrderStatus === RetailerOrderStatus.IN_REVIEW
        ) {
          this.$store
            .dispatch("retailerOrders/updateOrderStatus", {
              id: this.getOrder.id,
              status: RetailerOrderStatus.IN_PROGRESS
            })
            .then(() => {
              doReturn();
            });
        } else {
          doReturn();
        }
      }
    },
    saveOrder() {
      const updatedFitAdjustments = [];
      this.fitAdjustments.forEach(item => {
        updatedFitAdjustments.push(item);
      });

      this.$store
        .dispatch("retailerOrders/updateFitAdjustments", {
          id: this.getOrder.id,
          adjustments: updatedFitAdjustments
        })
        .then(() => {
          this.$store
            .dispatch("retailerOrders/updateOrderStatus", {
              id: this.getOrder.id,
              status: RetailerOrderStatus.IN_PROGRESS
            })
            .then(() => {
              this.toListScreen();
            });
        });
    },
    handleExport(selectedOptions) {
      this.showExportDialog = false;

      if (selectedOptions.type === "pdf") {
        exportProductionOrderToPdf(
          selectedOptions.language,
          this.$root.$i18n.messages,
          this.$store,
          {
            order: this.getOrder,
            isRtwTailoring: this.isRtwTailoring,
            adjustments: this.adjustments,
            fitAdjustments: this.fitAdjustments,
            imageBack: this.getImageBack,
            cropBack: this.getCropBack,
            imageSide: this.getImageSide,
            cropSide: this.getCropSide
          }
        );
      } else {
        exportProductionOrderToCsv(selectedOptions.language);
      }
    },
    finishOrder() {
      this.$store
        .dispatch("retailerOrders/updateOrderStatus", {
          id: this.getOrder.id,
          status: RetailerOrderStatus.IN_PRODUCTION
        })
        .then(() => {
          this.showFinishDialog = false;
          this.getViewData();
        });
    }
  },
  watch: {}
};
</script>
